// Shared renderer for CMS blog posts (see meltplan-cms/contract/delivery-contract.md).
// Used both by the React app (CmsArticle.js) and by the Node postbuild script
// (scripts/prerender-blog.js), so the prerendered HTML that crawlers see is exactly the
// markup — and Tailwind classes — the SPA renders. Keep it dependency-free (no imports):
// the postbuild script loads this file as a standalone ES module.

export const SITE_URL = 'https://costtoconstruct.com';
const PUBLISHER = {
  '@type': 'Organization',
  name: 'Cost to Construction',
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo_large.png` },
};

export const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

// Same-site absolute links become relative so the SPA can route them.
const localHref = (url) => (url && url.startsWith(SITE_URL) ? url.slice(SITE_URL.length) || '/' : url);

export function buildCmsMeta(post) {
  const canonical = `${SITE_URL}/blog/${post.slug}`;
  const title = post.metaTitle || post.title;
  const description = post.metaDescription || post.excerpt;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt,
      author: { '@type': 'Organization', name: post.author },
      publisher: PUBLISHER,
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      ...(post.cluster ? { articleSection: post.cluster.name } : {}),
      ...(post.targetKeyword ? { keywords: post.targetKeyword } : {}),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: canonical },
      ],
    },
  ];
  if (post.faqs && post.faqs.length) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }
  return {
    title,
    description,
    canonical,
    // [attribute, key, content] — rendered by Helmet in the SPA and as static tags by the prerenderer.
    tags: [
      ['property', 'og:type', 'article'],
      ['property', 'og:url', canonical],
      ['property', 'og:title', post.title],
      ['property', 'og:description', description],
      ['property', 'og:site_name', 'Cost to Construction'],
      ['name', 'twitter:card', 'summary'],
      ['name', 'twitter:title', post.title],
      ['name', 'twitter:description', description],
      ['property', 'article:published_time', post.publishedAt],
      ['property', 'article:modified_time', post.updatedAt],
      ...(post.cluster ? [['property', 'article:section', post.cluster.name]] : []),
    ],
    jsonLd,
  };
}

export function renderCmsArticleHtml(post) {
  const h2s = (post.toc || []).filter((t) => t.level === 2);
  const section = (id, heading, inner) =>
    `<section class="mt-14" id="${id}"><h2 class="text-2xl font-bold text-gray-900 mb-6">${heading}</h2>${inner}</section>`;

  const toc = h2s.length
    ? `<aside class="lg:col-span-1"><div class="lg:sticky lg:top-24 bg-white rounded-2xl border border-gray-200 p-6">` +
      `<p class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">On this page</p>` +
      `<ol class="space-y-3 text-sm">${h2s
        .map((t) => `<li><a href="#${esc(t.id)}" class="text-gray-600 hover:text-blue-600 leading-snug block">${esc(t.text)}</a></li>`)
        .join('')}` +
      `${post.faqs && post.faqs.length ? `<li><a href="#faq" class="text-gray-600 hover:text-blue-600 leading-snug block">FAQ</a></li>` : ''}</ol></div></aside>`
    : '';

  const takeaways = post.keyTakeaways && post.keyTakeaways.length
    ? `<div class="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-10"><p class="text-lg font-bold text-gray-900 mb-3">Key takeaways</p>` +
      `<ul class="list-disc pl-5 space-y-2 text-gray-800 leading-relaxed">${post.keyTakeaways.map((k) => `<li>${esc(k)}</li>`).join('')}</ul></div>`
    : '';

  const faqs = post.faqs && post.faqs.length
    ? section(
        'faq',
        'Frequently asked questions',
        `<div class="space-y-4">${post.faqs
          .map(
            (f) =>
              `<div class="bg-white border border-gray-200 rounded-xl p-6"><h3 class="text-lg font-semibold text-gray-900 mb-2">${esc(f.question)}</h3>` +
              `<p class="text-gray-700 leading-relaxed">${esc(f.answer)}</p></div>`,
          )
          .join('')}</div>`,
      )
    : '';

  const related = post.related && post.related.length
    ? section(
        'related-reading',
        'Related reading',
        `<ul class="grid grid-cols-1 sm:grid-cols-2 gap-4">${post.related
          .map(
            (r) =>
              `<li><a href="/blog/${esc(r.slug)}" class="block h-full bg-white border border-gray-200 hover:border-blue-300 rounded-xl p-5 font-semibold text-gray-900 hover:text-blue-600 transition-colors">${esc(r.title)}</a></li>`,
          )
          .join('')}</ul>`,
      )
    : '';

  const sources = post.sources && post.sources.length
    ? section(
        'sources',
        'Sources',
        `<ol class="list-decimal pl-5 space-y-2 text-sm text-gray-600">${post.sources
          .map(
            (s) =>
              `<li><a href="${esc(s.url)}" rel="noopener" target="_blank" class="text-blue-600 hover:underline">${esc(s.title)}</a>${s.publisher ? `, ${esc(s.publisher)}` : ''}</li>`,
          )
          .join('')}</ol>`,
      )
    : '';

  const tool = post.siteTool
    ? `<p class="mt-12 pt-8 border-t border-gray-200 text-gray-700">Next step: <a href="${esc(localHref(post.siteTool.url))}" class="font-semibold text-blue-600 hover:text-blue-700">${esc(post.siteTool.label)} →</a></p>`
    : '';

  const clusterName = post.cluster ? esc(post.cluster.name) : 'Guides';
  return (
    `<div class="min-h-screen bg-gradient-to-b from-gray-50 to-white">` +
    `<div class="bg-gradient-to-br from-teal-600 via-blue-600 to-indigo-700 text-white"><div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">` +
    `<nav aria-label="Breadcrumb" class="flex flex-wrap items-center text-sm text-blue-100 mb-6"><a href="/" class="hover:text-white">Home</a><span class="mx-2">›</span><a href="/blog" class="hover:text-white">Blog</a><span class="mx-2">›</span><span class="text-white font-medium">${clusterName}</span></nav>` +
    `<div class="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"><span class="font-semibold">${clusterName}</span></div>` +
    `<h1 class="text-4xl lg:text-5xl font-bold mb-4 leading-tight">${esc(post.title)}</h1>` +
    `<p class="text-lg text-blue-100 mb-6 max-w-3xl">${esc(post.excerpt)}</p>` +
    `<div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-blue-100"><span>${esc(post.author)}</span><span>Updated <time datetime="${esc(post.updatedAt)}">${formatDate(post.updatedAt)}</time></span><span>${post.readingMinutes} min read</span></div>` +
    `</div></div>` +
    `<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"><div class="grid grid-cols-1 lg:grid-cols-4 gap-12">` +
    toc +
    `<article class="${toc ? 'lg:col-span-3' : 'lg:col-span-4'} min-w-0">` +
    takeaways +
    `<div class="cms-prose">${post.bodyHtml}</div>` +
    faqs +
    related +
    sources +
    tool +
    `</article></div></div></div>`
  );
}

